import { useEffect, useState } from 'react';
import Button from '@shared/components/Button';
import ConfirmDialog from '@shared/components/ConfirmDialog';
import CustomDatePicker from '@shared/components/CustomDatePicker';
import DropdownSelect from '@shared/components/DropdownSelect';
import ModalShell from '@shared/components/ModalShell';
import OverflowMenu from '@shared/components/OverflowMenu';
import { useToast } from '@shared/contexts/ToastContext';
import { useDirtyTracker } from '@shared';
import AdminSaveButton from '@/components/AdminSaveButton';
import { SILENCE_MAX_DAYS, SILENCE_OPTIONS } from '@/lib/alarmLabels';
import { reloadAlarmSummary } from '@/lib/useAlarmSummary';
import { Icon } from '@shared';
import {
  acknowledgeAlarm,
  assignAlarm,
  getDevAdminUsers,
  resolveAlarm,
  silenceAlarm,
  unsilenceAlarm,
  type DevAdminUser,
  type MonitorAlarm,
  type MonitorAlarmActions,
  type SilenceDuration,
} from '@/services/api';

/* Los administradores para «Asignar»: una lista corta y fija, pedida una vez
 * por sesión (la comparten todas las alarmas abiertas). */
let adminUsersCache: Promise<DevAdminUser[]> | null = null;
function loadAdminUsers(): Promise<DevAdminUser[]> {
  if (!adminUsersCache) {
    adminUsersCache = getDevAdminUsers().then(
      (r) => r.users ?? [],
      (err: unknown) => {
        adminUsersCache = null;
        throw err;
      },
    );
  }
  return adminUsersCache;
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => {
  const v = String(h).padStart(2, '0');
  return { value: v, label: `${v}:00` };
});

const errMsg = (e: unknown) => (e instanceof Error && e.message ? e.message : 'No se pudo completar la acción');

/** «Hasta una fecha…»: fecha + hora locales, con su propio guardar. */
function SilenceUntilModal({
  isOpen,
  onClose,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (untilIso: string) => Promise<void>;
}) {
  const [date, setDate] = useState('');
  const [hour, setHour] = useState('09');
  const { isDirty, markClean } = useDirtyTracker(isOpen ? { date, hour } : null);

  useEffect(() => {
    if (!isOpen) {
      setDate('');
      setHour('09');
    }
  }, [isOpen]);

  const save = async () => {
    if (!date) throw new Error('Falta la fecha');
    const until = new Date(`${date}T${hour}:00:00`);
    if (Number.isNaN(until.getTime())) throw new Error('Fecha no válida');
    if (until.getTime() <= Date.now()) throw new Error('La fecha tiene que ser futura');
    if (until.getTime() > Date.now() + SILENCE_MAX_DAYS * 86_400_000) {
      throw new Error(`Como mucho ${SILENCE_MAX_DAYS} días`);
    }
    await onSave(until.toISOString());
    markClean();
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      dirty={isDirty}
      title="Silenciar hasta"
      subtitle={`Como mucho ${SILENCE_MAX_DAYS} días. Al terminar vuelve a su estado, o se reabre si se repitió.`}
      maxWidth="max-w-md"
      size="auto"
      zIndex="z-[120]"
      headerActions={<AdminSaveButton onSave={save} dirty={isDirty && !!date} saveLabel="Silenciar" />}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <CustomDatePicker label="Fecha" markRequired selectedDate={date} onChange={setDate} required />
        <DropdownSelect label="Hora" value={hour} onChange={setHour} options={HOUR_OPTIONS} variant="field" />
      </div>
    </ModalShell>
  );
}

interface Props {
  alarm: MonitorAlarm;
  actions: MonitorAlarmActions;
  /** Con la alarma que devuelve el servidor tras la acción. */
  onChanged: (alarm: MonitorAlarm) => void;
  /** `bar`: botones a lo ancho (barra inferior en móvil). */
  layout?: 'inline' | 'bar';
}

/**
 * Las acciones de UNA alarma: Reconocer, Resolver, Silenciar, Reactivar y
 * Asignar. Cada botón sale sólo si el servidor dice que se puede
 * (`actions`). Tras cada acción se refrescan los contadores del menú.
 */
export default function AlarmActions({ alarm, actions, onChanged, layout = 'inline' }: Props) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [untilOpen, setUntilOpen] = useState(false);
  const [adminUsers, setAdminUsers] = useState<DevAdminUser[] | null>(null);

  useEffect(() => {
    if (!actions.assign) return;
    let cancelled = false;
    loadAdminUsers()
      .then((users) => {
        if (!cancelled) setAdminUsers(users);
      })
      .catch(() => {
        if (!cancelled) setAdminUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [actions.assign]);

  const run = async (fn: () => Promise<{ alarm: MonitorAlarm }>): Promise<boolean> => {
    setBusy(true);
    try {
      const res = await fn();
      onChanged(res.alarm);
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    } finally {
      setBusy(false);
      reloadAlarmSummary().catch(() => { /* el fallo queda en el almacén */ });
    }
  };

  const silence = (duration: SilenceDuration) => {
    if (duration === 'until') {
      setUntilOpen(true);
      return;
    }
    void run(() => silenceAlarm(alarm.id, duration));
  };

  const saveUntil = async (untilIso: string) => {
    // El modal muestra su propio error: aquí se relanza en vez de tostar.
    setBusy(true);
    try {
      const res = await silenceAlarm(alarm.id, 'until', untilIso);
      onChanged(res.alarm);
      setUntilOpen(false);
    } finally {
      setBusy(false);
      reloadAlarmSummary().catch(() => { /* idem */ });
    }
  };

  const userOptions = [
    { value: '', label: 'Sin asignar' },
    ...(adminUsers ?? []).map((u) => {
      const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
      return {
        value: String(u.id),
        label: name || u.email,
        sublabel: name ? u.email : undefined,
        avatarUrl: u.avatar_url || null,
      };
    }),
  ];
  // La asignada puede no estar en la lista (dejó de ser administradora): se
  // añade para que el selector no la pinte como «Sin asignar».
  if (alarm.assigned_to && !userOptions.some((o) => o.value === String(alarm.assigned_to?.id))) {
    userOptions.push({
      value: String(alarm.assigned_to.id),
      label: alarm.assigned_to.name || alarm.assigned_to.email,
      sublabel: alarm.assigned_to.name ? alarm.assigned_to.email : undefined,
      avatarUrl: alarm.assigned_to.avatar_url || null,
    });
  }

  const wide = layout === 'bar' ? 'flex-1' : '';

  return (
    <div className={`flex flex-wrap items-center gap-2 ${layout === 'bar' ? 'w-full' : ''}`}>
      {actions.acknowledge ? (
        <Button type="button" variant="primary" icon="visibility" disabled={busy} className={wide}
          onClick={() => void run(() => acknowledgeAlarm(alarm.id))}>
          Reconocer
        </Button>
      ) : null}
      {actions.unsilence ? (
        <Button type="button" variant="secondary" icon="notifications_active" disabled={busy} className={wide}
          onClick={() => void run(() => unsilenceAlarm(alarm.id))}>
          Reactivar
        </Button>
      ) : null}
      {actions.resolve ? (
        <Button type="button" variant="success" icon="check_circle" disabled={busy} className={wide}
          onClick={() => setResolveOpen(true)}>
          Resolver
        </Button>
      ) : null}
      {actions.silence ? (
        <OverflowMenu
          ariaLabel="Silenciar"
          items={SILENCE_OPTIONS.map((o) => ({
            key: o.value,
            icon: o.value === 'until' ? 'event' : 'schedule',
            label: o.label,
            disabled: busy,
            onClick: () => silence(o.value),
          }))}
          renderTrigger={() => (
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-border-subtle dark:border-border-subtle-dark px-3 py-2 text-sm font-semibold text-text-main dark:text-white hover:bg-slate-50 dark:hover:bg-surface-hover-dark">
              <Icon name="notifications_off" className="!text-[18px]" aria-hidden="true" />
              Silenciar
              <Icon name="expand_more" className="!text-[18px]" aria-hidden="true" />
            </span>
          )}
        />
      ) : null}
      {actions.assign ? (
        <div className={layout === 'bar' ? 'w-full' : 'w-56'}>
          <DropdownSelect
            variant="field"
            searchable
            searchPlaceholder="Buscar administrador…"
            placeholder="Sin asignar"
            ariaLabel="Asignar a"
            icon="person"
            value={alarm.assigned_to ? String(alarm.assigned_to.id) : ''}
            options={userOptions}
            disabled={busy || adminUsers === null}
            onChange={(v) => {
              const next = v ? Number(v) : null;
              if (next === (alarm.assigned_to?.id ?? null)) return;
              void run(() => assignAlarm(alarm.id, next));
            }}
          />
        </div>
      ) : null}

      <ConfirmDialog
        isOpen={resolveOpen}
        onClose={() => setResolveOpen(false)}
        onConfirm={() => {
          void run(() => resolveAlarm(alarm.id)).then((ok) => {
            if (ok) setResolveOpen(false);
          });
        }}
        loading={busy}
        variant="primary"
        confirmLabel="Resolver"
        title="Resolver la alarma"
        message={
          alarm.stateful
            ? 'Se marca como resuelta. Si la condición sigue, volverá a abrirse cuando pase la ventana de reapertura.'
            : 'Se marca como resuelta. Si el problema se repite, se abrirá de nuevo.'
        }
      />
      <SilenceUntilModal isOpen={untilOpen} onClose={() => setUntilOpen(false)} onSave={saveUntil} />
    </div>
  );
}
